/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Directive, ElementRef, Input, AfterViewInit} from '@angular/core';
/**
 * Directive to automatically resize a textarea to fit its content.
 */
export class MdTextareaAutosize implements AfterViewInit {
private _minRows: number;
private _maxRows: number;
/**
 * @return {?}
 */
get minRows() { return this._minRows; }
/**
 * @param {?} value
 * @return {?}
 */
set minRows(value: number) {
    this._minRows = value;
    this._setMinHeight();
  }
/**
 * @return {?}
 */
get maxRows() { return this._maxRows; }
/**
 * @param {?} value
 * @return {?}
 */
set maxRows(value: number) {
    this._maxRows = value;
    this._setMaxHeight();
  }
/**
 * @return {?}
 */
get _matAutosizeMinRows() { return this.minRows; }
/**
 * @param {?} v
 * @return {?}
 */
set _matAutosizeMinRows(v) { this.minRows = v; }
/**
 * @return {?}
 */
get _matAutosizeMaxRows() { return this.maxRows; }
/**
 * @param {?} v
 * @return {?}
 */
set _matAutosizeMaxRows(v) { this.maxRows = v; }
/**
 * Cached height of a textarea with a single row.
 */
private _cachedLineHeight: number;
/**
 * @param {?} _elementRef
 */
constructor(private _elementRef: ElementRef) { }
/**
 * Sets the minimum height of the textarea as determined by minRows.
 * @return {?}
 */
_setMinHeight(): void {
    const /** @type {?} */ minHeight = this.minRows && this._cachedLineHeight ?
        `${this.minRows * this._cachedLineHeight}px` : null;

    if (minHeight)  {
      this._setTextareaStyle('minHeight', minHeight);
    }
  }
/**
 * Sets the maximum height of the textarea as determined by maxRows.
 * @return {?}
 */
_setMaxHeight(): void {
    const /** @type {?} */ maxHeight = this.maxRows && this._cachedLineHeight ?
        `${this.maxRows * this._cachedLineHeight}px` : null;

    if (maxHeight) {
      this._setTextareaStyle('maxHeight', maxHeight);
    }
  }
/**
 * @return {?}
 */
ngAfterViewInit() {
    this._cacheTextareaLineHeight();
    this.resizeToFitContent();
  }
/**
 * Sets a style property on the textarea element.
 * @param {?} property
 * @param {?} value
 * @return {?}
 */
private _setTextareaStyle(property: string, value: string): void {
    const /** @type {?} */ textarea = /** @type {?} */(( this._elementRef.nativeElement as HTMLTextAreaElement));
    textarea.style[property] = value;
  }
/**
 * Cache the height of a single-row textarea.
 * 
 * We need to know how large a single "row" of a textarea is in order to apply minRows and
 * maxRows. For the initial version, we will assume that the height of a single line in the
 * textarea does not ever change.
 * @return {?}
 */
private _cacheTextareaLineHeight(): void {
    let /** @type {?} */ textarea = /** @type {?} */(( this._elementRef.nativeElement as HTMLTextAreaElement));

    // Use a clone element because we have to override some styles.
    let /** @type {?} */ textareaClone = /** @type {?} */(( textarea.cloneNode(false) as HTMLTextAreaElement));
    textareaClone.rows = 1;

    // Use `position: absolute` so that this doesn't cause a browser layout and use
    // `visibility: hidden` so that nothing is rendered. Clear any other styles that
    // would affect the height.
    textareaClone.style.position = 'absolute';
    textareaClone.style.visibility = 'hidden';
    textareaClone.style.border = 'none';
    textareaClone.style.padding = '0';
    textareaClone.style.height = '';
    textareaClone.style.minHeight = '';
    textareaClone.style.maxHeight = '';

    textarea.parentNode.appendChild(textareaClone);
    this._cachedLineHeight = textareaClone.clientHeight;
    textarea.parentNode.removeChild(textareaClone);

    // Min and max heights have to be re-calculated if the cached line height changes
    this._setMinHeight();
    this._setMaxHeight();
  }
/**
 * Resize the textarea to fit its content.
 * @return {?}
 */
resizeToFitContent() {
    const /** @type {?} */ textarea = /** @type {?} */(( this._elementRef.nativeElement as HTMLTextAreaElement));

    // Reset the textarea height to auto in order to shrink back to its default size.
    textarea.style.height = 'auto';

    // Use the scrollHeight to know how large the textarea *would* be if fit its entire value.
    textarea.style.height = `${textarea.scrollHeight}px`;
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: 'textarea[md-autosize], textarea[mdTextareaAutosize],' +
            'textarea[mat-autosize], textarea[matTextareaAutosize]',
  exportAs: 'mdTextareaAutosize',
  host: {
    '(input)': 'resizeToFitContent()',
    // Textarea elements that have the directive applied should have a single row by default.
    // Browsers normally show two rows by default and therefore this limits the minRows binding.
    'rows': '1',
  },
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'minRows': [{ type: Input, args: ['mdAutosizeMinRows', ] },],
'maxRows': [{ type: Input, args: ['mdAutosizeMaxRows', ] },],
'_matAutosizeMinRows': [{ type: Input, args: ['matAutosizeMaxRows', ] },],
'_matAutosizeMaxRows': [{ type: Input, args: ['matAutosizeMaxRows', ] },],
};
}

function MdTextareaAutosize_tsickle_Closure_declarations() {
/** @type {?} */
MdTextareaAutosize.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdTextareaAutosize.ctorParameters;
/** @type {?} */
MdTextareaAutosize.propDecorators;
/** @type {?} */
MdTextareaAutosize.prototype._minRows;
/** @type {?} */
MdTextareaAutosize.prototype._maxRows;
/**
 * Cached height of a textarea with a single row.
 * @type {?}
 */
MdTextareaAutosize.prototype._cachedLineHeight;
/** @type {?} */
MdTextareaAutosize.prototype._elementRef;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
